import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/frontend-plugin-api';
import { BackendAuthApi } from '@internal/plugin-neo4j-auth-common';
import {
  Configuration,
  FetchError,
  OperatorsOperatorNameRolloutsRolloutIdentifierClusterRolloutsGetRequest,
  ResponseClusterRollout,
  ResponseClusterRolloutList,
  ResponseError,
  ResponseRollout,
  ResponseRolloutSummaryList,
  RolloutApi,
} from '@internal/backstage-plugin-neo4j-rollouts-common';
import { RolloutApiError } from './errors';

const PROXY_PATH = 'controller-rollout-api';

export type ListClusterRolloutsOptions = Omit<
  OperatorsOperatorNameRolloutsRolloutIdentifierClusterRolloutsGetRequest,
  'operatorName' | 'rolloutIdentifier'
>;

export class RolloutRestApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly backendAuthApi: BackendAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    backendAuthApi: BackendAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.backendAuthApi = options.backendAuthApi;
  }

  private async getRolloutApi(): Promise<RolloutApi> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    // The proxy consumes Authorization for the Backstage session token, so
    // the user's token travels in this header (forwarded via allowedHeaders
    // in app-config.yaml); the API reads it there.
    const token = await this.backendAuthApi.getToken();

    return new RolloutApi(
      new Configuration({
        basePath: `${baseUrl}/${PROXY_PATH}/v1`,
        fetchApi: this.fetchApi.fetch,
        headers: token ? { 'Aura-Portal-Okta-Token': token } : undefined,
      }),
    );
  }

  async listRollouts(): Promise<ResponseRolloutSummaryList> {
    try {
      const api = await this.getRolloutApi();
      return await api.rolloutsGet();
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getRollout(
    operatorName: string,
    rolloutIdentifier: string,
  ): Promise<ResponseRollout> {
    try {
      const api = await this.getRolloutApi();
      return await api.operatorsOperatorNameRolloutsRolloutIdentifierGet({
        operatorName,
        rolloutIdentifier,
      });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async listClusterRollouts(
    operatorName: string,
    rolloutIdentifier: string,
    options: ListClusterRolloutsOptions = {},
  ): Promise<ResponseClusterRolloutList> {
    try {
      const api = await this.getRolloutApi();
      return await api.operatorsOperatorNameRolloutsRolloutIdentifierClusterRolloutsGet(
        { operatorName, rolloutIdentifier, ...options },
      );
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getClusterRollout(
    operatorName: string,
    rolloutIdentifier: string,
    clusterName: string,
  ): Promise<ResponseClusterRollout> {
    try {
      const api = await this.getRolloutApi();
      return await api.operatorsOperatorNameRolloutsRolloutIdentifierClusterRolloutsClusterNameGet(
        { operatorName, rolloutIdentifier, clusterName },
      );
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  private async handleSdkError(error: unknown): Promise<never> {
    if (error instanceof ResponseError) {
      const { response } = error;
      let message = error.message || response.statusText;
      let code: number | undefined;

      try {
        const json = await response.json();
        if (typeof json.message === 'string') {
          message = json.message;
        }
        if (typeof json.code === 'number') {
          code = json.code;
        }
      } catch {
        // Failed to parse the error body, keep the default message
      }

      throw new RolloutApiError(message, response.status, code);
    }

    // Network failures surface as FetchError wrapping the real cause —
    // unwrap instead of fabricating an HTTP status.
    if (error instanceof FetchError) {
      throw error.cause;
    }

    throw error;
  }
}

export const rolloutApiRef: ApiRef<RolloutRestApi> =
  createApiRef<RolloutRestApi>({
    id: 'plugin.neo4j-rollouts.service',
  });
